/* eslint-disable max-len */
import { useLazyQuery, useMutation } from '@apollo/client';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { deleteAssayInput, DELETE_ASSAY, getAssayOutput, getAssaysInput, GET_ASSAY } from '../../../api';
import { DeleteConfirmation, globalPageSizeOptions, PaginationBar, PhotoModal } from '../../../components';
import { assay } from '../../../entityModel';
import { useApolloErrorHandler, useAuthServices } from '../../../hooks';
import { capitalizeFirstLetterEveryword } from '../../../shared/stringFunctions';
import { TXT_LIST_ASSAY, TXT_SEARCH_BY_NAME } from '../../../translations/translationConstants';
import { ManageAssaysTable } from './ManageAssayTable';

interface ManageAssaysProps {
  boreholeId?: string;
}

export const ManageAssays = () => {
  const { t } = useTranslation();
  const params = useParams<ManageAssaysProps>();
  const { isSignedIn } = useAuthServices();

  const [selectedAssay, setSelectedAssay] = useState<assay | undefined>(undefined);

  const [getAssays, { loading, data, error, refetch }] = useLazyQuery<getAssayOutput>(GET_ASSAY);

  const [openDeleteConfirmation, setOpenDeleteConfirmation] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState(globalPageSizeOptions[0]);
  const [openEditor, setOpenEditor] = useState(false);
  const [openPhoto, setOpenPhoto] = useState(false);
  const [txtSearch, setTxtSearch] = useState('');

  const lblSearch = capitalizeFirstLetterEveryword(t(TXT_SEARCH_BY_NAME));

  const onTxtSearchChanged = (event: React.ChangeEvent<HTMLInputElement>) => {
    setTxtSearch(event.currentTarget.value);
  };

  const fetchAssays = () => {
    const variables: getAssaysInput = {
      code: txtSearch,
      take: pageSize,
      skip: (pageNumber - 1) * pageSize,
      filter: {
        boreholeId: {
          eq: params.boreholeId
        }
      }
    };
    getAssays({
      variables
    });
  };

  useEffect(() => {
    useApolloErrorHandler(isSignedIn, error);
  }, [error]);

  useEffect(() => {
    fetchAssays();
  }, [data, pageNumber]);

  const onOpenDeleteConfirmation = (assay: assay) => {
    setSelectedAssay(assay);
    setOpenDeleteConfirmation(true);
  };

  const onDeleteCancel = () => setOpenDeleteConfirmation(false);

  const [deleteAssay, deleteAssayOperation] = useMutation<boolean>(DELETE_ASSAY, {
    onError: (e) => { },
    onCompleted: (result) => {
      if (data && refetch) {
        if (data.assays.items.length == 1) {
          const targetPage = pageNumber - 1 <= 0 ? 1 : pageNumber - 1;
          if (pageNumber !== targetPage) {
            setPageNumber(targetPage);
          } else {
            refetch();
          }
        } else {
          refetch();
        }
      }
      onDeleteCancel();
    }
  });

  const onDelete = () => {
    const input: deleteAssayInput = {
      id: selectedAssay!.id!
    };
    deleteAssay({
      variables: {
        input
      }
    });
  };

  const onOpenEditAssayEditor = (assay: assay) => {
    setSelectedAssay(assay);
    setOpenEditor(true);
  };

  const onOpenPhotoAssay = (assay: assay) => {
    setSelectedAssay(assay);
    setOpenPhoto(true);
  };

  const onPageSizeChanged = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setPageSize(+event.target.value);
  };

  return (
    <div className="content-wrapper">
      <section className="content-header">
        <div className="container-fluid">
          <div className="row mb-2">
            <div className="col-sm-6">
              <h1>{t(capitalizeFirstLetterEveryword(TXT_LIST_ASSAY))}</h1>
            </div>
          </div>
        </div>
      </section>
      <section className="content">
        <div className="container-fluid">
          {
            openEditor &&
            { alert }
          }
          {
            openPhoto &&
            <PhotoModal
              objectName={selectedAssay?.drillCard || ''}
              filePath={selectedAssay?.filePath || ''}
            />
          }
          {
            openDeleteConfirmation &&
            <DeleteConfirmation
              loading={deleteAssayOperation.loading}
              onYes={onDelete}
              onNo={onDeleteCancel}
              objectName={selectedAssay?.code || ''}
            />
          }
          <div className="row">
            <div className="col-12">
              <div className="card">
                <div className="card-header">
                  <div className='row'>
                    <div className='col-6'>
                      <div className="form-group">
                        <label>{lblSearch}</label>
                        <input type='text' className='form-control' value={txtSearch} onChange={onTxtSearchChanged} />
                      </div>
                    </div>
                  </div>
                </div>
                <div className="card-body">
                  <ManageAssaysTable
                    loading={loading}
                    data={data}
                    error={error}
                    onEdit={onOpenEditAssayEditor}
                    onDelete={onOpenDeleteConfirmation}
                    onOpenPhoto={onOpenPhotoAssay}
                  />
                </div>
                <PaginationBar
                  currentPage={pageNumber}
                  totalCount={data?.assays.totalCount}
                  pageSize={pageSize}
                  onPageChanged={(page) => setPageNumber(page)}
                  onChangeRowsPerPage={onPageSizeChanged}
                />
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};
